/**
 * WFS Query Cache and Optimization Utilities
 * 
 * This module provides caching and optimization for WFS queries to improve
 * frontend performance by reducing redundant API calls.
 */

class WFSCache {
  constructor(maxEntries = 100, ttl = 300000) { // 5 minutes TTL
    this.cache = new Map();
    this.maxEntries = maxEntries;
    this.ttl = ttl;
  }

  /**
   * Generate a cache key based on bbox and filters
   */
  generateKey(bbox, filters, layerType) {
    const sortedFilters = JSON.stringify(filters, Object.keys(filters).sort());
    return `${layerType}:${bbox}:${sortedFilters}`;
  }

  /**
   * Get cached result if still valid
   */
  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set cache entry
   */
  set(key, data) {
    // Remove oldest entries if cache is full
    if (this.cache.size >= this.maxEntries) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Clear all cache entries
   */
  clear() {
    this.cache.clear();
  }

  /**
   * Clear entries for specific layer type
   */
  clearLayer(layerType) {
    for (const [key] of this.cache) {
      if (key.startsWith(`${layerType}:`)) {
        this.cache.delete(key);
      }
    }
  }
}

// Create singleton cache instance
export const wfsCache = new WFSCache();

/**
 * Debounce function to prevent excessive API calls
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Check if two bounding boxes have significant overlap
 * to avoid redundant queries for similar areas
 */
export function bboxOverlap(bbox1, bbox2, threshold = 0.8) {
  const [w1, s1, e1, n1] = bbox1.split(',').map(Number);
  const [w2, s2, e2, n2] = bbox2.split(',').map(Number);

  const overlapW = Math.max(w1, w2);
  const overlapS = Math.max(s1, s2);
  const overlapE = Math.min(e1, e2);
  const overlapN = Math.min(n1, n2);

  if (overlapW >= overlapE || overlapS >= overlapN) return false;

  const overlapArea = (overlapE - overlapW) * (overlapN - overlapS);
  const bbox1Area = (e1 - w1) * (n1 - s1);

  return overlapArea / bbox1Area > threshold;
}

/**
 * Abort controller manager for canceling in-flight requests
 */
export class RequestManager {
  constructor() {
    this.controllers = new Map();
  }

  /**
   * Create new abort controller for a request type
   */
  createController(requestType) {
    // Cancel existing request of same type
    this.cancelRequest(requestType);
    
    const controller = new AbortController();
    this.controllers.set(requestType, controller);
    return controller;
  }

  /**
   * Cancel specific request type
   */
  cancelRequest(requestType) {
    const controller = this.controllers.get(requestType);
    if (controller) {
      controller.abort();
      this.controllers.delete(requestType);
    }
  }

  /**
   * Cancel all requests
   */
  cancelAllRequests() {
    for (const controller of this.controllers.values()) {
      controller.abort();
    }
    this.controllers.clear();
  }
}

export const requestManager = new RequestManager();